/* global process */
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { Box, Typography, CircularProgress, TextField, Dialog, useMediaQuery } from "@mui/material";
import {
  User, MapPin, Briefcase, FileText, Globe,
  CheckCircle, ChevronDown, ChevronUp, Plus, X, Trash2,
  Upload, Camera,
  Eye, TrendingUp, Target, Mail, Phone, Download,
  GraduationCap, AlertCircle, Shield, Link as LinkIcon, Award,
  FolderOpen, Share2, Copy, Pencil,
} from "lucide-react";
import axios from "axios";
import sh from "bundles/common/utils/sh";
import FileDropzone from "bundles/common/components/FileDropzone";
import SkillSelect from "bundles/common/components/SkillSelect";
import CertificateSelect from "bundles/common/components/CertificateSelect";
import translations from "app/libs/i18n/translations.json";
import Autocomplete from "react-google-autocomplete";
import { replaceCurrentCandidate } from "bundles/Candidate/constants/candidateConstants";
import {
  directUpload,
  uploadResume,
  uploadPortfolio,
  attachPortfolioToCandidate,
} from "bundles/common/utils/uploadDocument";
import { fileChecksum } from "bundles/common/utils/fileChecksum";
import { companyIndustryMap, candidateStatus } from "bundles/common/utils/constants";
import { normalizeRegion } from "bundles/common/utils/utils";

const cleanLocation = (loc) => (loc || "").split(",")[0].replace(/^\d+\s*/, "").trim();

const toDesiredLocation = (loc) => (
  typeof loc === "string" ? { label: cleanLocation(loc) } : { label: loc?.label, region: loc?.region, country: loc?.country }
);

const regionFromPlace = (place) => {
  let adminLevel2 = "";
  let adminLevel1 = "";
  let country = "";
  (place.address_components || []).forEach((c) => {
    if (c.types.includes("administrative_area_level_2")) adminLevel2 = c.long_name?.toLowerCase()?.replace(/ /g, "_");
    if (c.types.includes("administrative_area_level_1")) adminLevel1 = c.long_name?.toLowerCase()?.replace(/ /g, "_");
    if (c.types.includes("country")) country = c.short_name.toLowerCase();
  });
  let region = "";
  if (country === "fr") {
    region = normalizeRegion(adminLevel1 || adminLevel2);
  } else if (country === "be") {
    region = adminLevel2 === "luxembourg" ? "be_luxembourg" : (adminLevel2 || adminLevel1);
  } else {
    region = adminLevel2 || adminLevel1;
  }
  return { region: region || undefined, country: country || undefined };
};

// PUT the file to the presigned URL via XHR so we can report upload progress.
const uploadFileWithProgress = (url, headers, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });

const getSkillLabel = (category, locale, id) => {
  const t = translations[locale] || translations.fr || {};
  return t[`skills_list.${category}.${id}`] || id;
};

const getCertificateLabel = (locale, name) => {
  const t = translations[locale] || translations.fr || {};
  for (const sector of ["construction", "architecture", "engineering", "industry"]) {
    const v = t[`certificates_list.${sector}.${name}`];
    if (v) return v;
  }
  return name;
};

const cardSx = {
  bgcolor: "hsl(0, 0%, 100%)",
  borderRadius: "16px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  overflow: "hidden",
};

const inputSx = {
  width: "100%",
  height: "44px",
  borderRadius: "12px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  bgcolor: "hsla(160, 10%, 96%, 0.3)",
  px: "8px",
  fontSize: "13px",
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(178, 93%, 10%)",
  outline: "none",
  "&:focus": { borderColor: "hsla(153, 68%, 25%, 0.4)" },
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

const labelSx = {
  fontSize: "11px",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  color: "hsl(178, 10%, 45%)",
  mb: "6px",
  display: "block",
};

const textFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "44px",
    borderRadius: "12px",
    bgcolor: "hsla(160, 10%, 96%, 0.3)",
    fontSize: "13px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 93%, 10%)",
    transition: "border-color 0.15s",
    "& fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
    "&:hover fieldset": { borderColor: "hsla(160, 10%, 90%, 0.6)" },
    "&.Mui-focused fieldset": { borderColor: "hsla(153, 68%, 25%, 0.4)", borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { px: "8px", py: 0 },
  "& .MuiOutlinedInput-multiline": { height: "auto", py: "10px" },
};

const textFieldErrorSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    bgcolor: "hsla(0, 65%, 51%, 0.04)",
    "& fieldset": { borderColor: "hsl(0, 65%, 51%)" },
    "&:hover fieldset": { borderColor: "hsl(0, 65%, 51%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(0, 65%, 51%)", borderWidth: "1px" },
  },
};

const inputErrorSx = {
  ...inputSx,
  borderColor: "hsl(0, 65%, 51%)",
  bgcolor: "hsla(0, 65%, 51%, 0.04)",
  "&:focus": { borderColor: "hsl(0, 65%, 51%)" },
};

const FieldError = ({ message }) => (
  <Typography sx={{ fontSize: "11px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "4px" }}>
    {message}
  </Typography>
);

// Multiline variant: the fixed 44px height in textFieldSx clips a textarea and
// pushes the text to the top, so description fields need an auto-height root.
const textAreaSx = {
  ...textFieldSx,
  "& .MuiOutlinedInput-root": {
    ...textFieldSx["& .MuiOutlinedInput-root"],
    height: "auto",
    py: "10px",
  },
  "& .MuiOutlinedInput-input": { px: "8px", py: 0 },
};

const PROJECT_PHOTO_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpeg", ".jpg"],
  "image/svg+xml": [".svg"],
};

const skillChipSx = (variant = "hard") => {
  const colors = {
    hard: { bg: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)" },
    soft: { bg: "hsla(178, 93%, 10%, 0.1)", color: "hsl(178, 93%, 10%)" },
    lang: { bg: "hsla(178, 10%, 45%, 0.1)", color: "hsl(178, 10%, 45%)" },
  };
  const c = colors[variant] || colors.hard;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    borderRadius: "9999px",
    bgcolor: c.bg,
    color: c.color,
    fontSize: "12px",
    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  };
};

const previewSkillSx = (variant = "hard") => {
  const colors = {
    hard: { bg: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)" },
    soft: { bg: "hsl(178, 93%, 10%)", color: "hsl(0, 0%, 100%)" },
    lang: { bg: "hsla(178, 10%, 45%, 0.15)", color: "hsl(178, 10%, 45%)" },
  };
  const c = colors[variant] || colors.hard;
  return {
    px: "14px",
    py: "8px",
    borderRadius: "9999px",
    bgcolor: c.bg,
    color: c.color,
    fontSize: "13px",
    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  };
};

const btnPrimarySx = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
  py: "10px", borderRadius: "12px", border: "none", cursor: "pointer",
  bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px",
  "&:hover": { bgcolor: "hsl(153, 68%, 30%)" },
  transition: "all 0.15s",
};

const btnSecondarySx = {
  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
  py: "10px", px: "16px", borderRadius: "12px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)", cursor: "pointer",
  bgcolor: "transparent", color: "hsl(178, 10%, 45%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px",
  "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.3)" },
  transition: "all 0.15s",
};

const DEGREE_OPTIONS = [
  { value: "", label: "—" },
  { value: "no_degree", labelId: "candidate.profile_tab.degree.no_degree" },
  { value: "highschool", labelId: "candidate.profile_tab.degree.highschool" },
  { value: "cap", labelId: "candidate.profile_tab.degree.cap" },
  { value: "bts", labelId: "candidate.profile_tab.degree.bts" },
  { value: "bachelor", labelId: "candidate.profile_tab.degree.bachelor" },
  { value: "master", labelId: "candidate.profile_tab.degree.master" },
  { value: "phd", labelId: "candidate.profile_tab.degree.phd" },
  { value: "other_training", labelId: "candidate.profile_tab.degree.other" },
];

const AVAILABILITY_OPTIONS = [
  { value: "", label: "—" },
  { value: "available", labelId: "candidate.profile_tab.availability.available" },
  { value: "listening", labelId: "candidate.profile_tab.availability.listening" },
  { value: "currently_employed", labelId: "candidate.profile_tab.availability.currently_employed" },
  { value: "unavailable", labelId: "candidate.profile_tab.availability.unavailable" },
];

const PROJECT_TYPE_OPTIONS = [
  { value: "", label: "—" },
  { value: "residential", labelId: "candidate.profile_tab.project_type.residential" },
  { value: "commercial", labelId: "candidate.profile_tab.project_type.commercial" },
  { value: "industrial", labelId: "candidate.profile_tab.project_type.industrial" },
  { value: "public", labelId: "candidate.profile_tab.project_type.public" },
  { value: "renovation", labelId: "candidate.profile_tab.project_type.renovation" },
  { value: "interior", labelId: "candidate.profile_tab.project_type.interior" },
  { value: "landscape", labelId: "candidate.profile_tab.project_type.landscape" },
  { value: "infrastructure", labelId: "candidate.profile_tab.project_type.infrastructure" },
  { value: "other", labelId: "candidate.profile_tab.project_type.other" },
];

const ProfileTab = ({ sectionRequest = null, onSectionRequestConsumed }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const sectorOptions = useMemo(() => companyIndustryMap.map((s) => ({
    value: s,
    label: intl.formatMessage({ id: `company.company_industry.${s}`, defaultMessage: s }),
  })), [intl]);

  const statusOptions = useMemo(() => [
    { value: "", label: "—" },
    ...candidateStatus.map((s) => ({
      value: s,
      label: intl.formatMessage({ id: `candidate.status_map.${s}`, defaultMessage: s }),
    })),
  ], [intl]);

  const f = useCallback((id, values) => intl.formatMessage({ id: `candidate.profile_tab.${id}` }, values), [intl]);

  const localizeOptions = useCallback(
    (opts) => opts.map((o) => ({ value: o.value, label: o.labelId ? intl.formatMessage({ id: o.labelId }) : o.label })),
    [intl]
  );
  const degreeOptions = useMemo(() => localizeOptions(DEGREE_OPTIONS), [localizeOptions]);
  const availabilityOptions = useMemo(() => localizeOptions(AVAILABILITY_OPTIONS), [localizeOptions]);
  const projectTypeOptions = useMemo(() => localizeOptions(PROJECT_TYPE_OPTIONS), [localizeOptions]);

  const [form, setForm] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [quickFixField, setQuickFixField] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { run } — pending destructive action
  const saveStatusTimerRef = useRef(null);
  const formRef = useRef({});
  const [desiredLocations, setDesiredLocations] = useState([]);
  const [hardSkills, setHardSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [langSkills, setLangSkills] = useState([]);
  const [savingCertificate, setSavingCertificate] = useState(false);
  const [mobileView, setMobileView] = useState("edit");
  const formDirty = useRef(false);
  const saveTimerRef = useRef(null);
  const locationInputRef = useRef(null);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [expandedSections, setExpandedSections] = useState(
    new Set(["basic", "headline", "experience", "skills"])
  );

  const [showExpForm, setShowExpForm] = useState(false);
  const [editingExpId, setEditingExpId] = useState(null);
  const [expForm, setExpForm] = useState({ title: "", company: "", location: "", start_date: "", end_date: "", current: false, description: "" });
  const [savingExp, setSavingExp] = useState(false);
  const [expTouched, setExpTouched] = useState({});
  const [expSubmitAttempted, setExpSubmitAttempted] = useState(false);

  const [showTrainingForm, setShowTrainingForm] = useState(false);
  const [editingTrainingId, setEditingTrainingId] = useState(null);
  const [trainingForm, setTrainingForm] = useState({ diploma: "", institution: "", study_field: "", location: "", start_date: "", end_date: "" });
  const [savingTraining, setSavingTraining] = useState(false);
  const [trainingTouched, setTrainingTouched] = useState({});
  const [trainingSubmitAttempted, setTrainingSubmitAttempted] = useState(false);

  const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const projectPhotoInputRef = useRef(null);

  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectForm, setProjectForm] = useState({ title: "", project_type: "", location: "", end_date: "", description: "" });
  const [savingProject, setSavingProject] = useState(false);
  // Each pending photo: { uid, previewUrl, progress, status: "uploading"|"done"|"error", blobSignedId }
  const [pendingProjectPhotos, setPendingProjectPhotos] = useState([]);
  const [togglingShare, setTogglingShare] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // CV modal — commented out for now
  // const [showCvModal, setShowCvModal] = useState(false);

  useEffect(() => {
    if (!currentCandidate) return;
    setForm({
      first_name: currentCandidate.first_name || "",
      last_name: currentCandidate.last_name || "",
      email: currentCandidate.email || "",
      job_title: currentCandidate.job_title || "",
      phone: currentCandidate.phone || "",
      city: currentCandidate.city || "",
      region: currentCandidate.region || "",
      country: currentCandidate.country || "",
      zipcode: currentCandidate.zipcode || "",
      presentation: currentCandidate.presentation || "",
      availability: currentCandidate.availability || "",
      experience_year: currentCandidate.experience_year || "",
      desired_job_title: currentCandidate.desired_job_title || "",
      desired_sector: currentCandidate.desired_sector || [],
      work_permit: currentCandidate.work_permit ?? true,
      highest_degree: currentCandidate.highest_degree || "",
      portfolio_url: currentCandidate.portfolio_url || "",
      candidate_status: currentCandidate.candidate_status || "",
    });
    setHardSkills((currentCandidate.hard_skills || []).map((s) => s.name));
    setSoftSkills((currentCandidate.soft_skills || []).map((s) => s.name));
    setLangSkills((currentCandidate.language_skills || []).map((s) => s.name));
    setDesiredLocations((currentCandidate.desired_locations || []).map(toDesiredLocation).filter((l) => l.label));
    formDirty.current = false;
  }, [currentCandidate?.id]);

  useEffect(() => { formRef.current = form; }, [form]);

  useEffect(() => {
    if (!formDirty.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      // Own cancel key: sh cancels by URL, and other /candidate saves must not cancel this one.
      sh.put(
        "/candidate",
        { authenticity_token: authenticityToken, candidate: { ...form, id: currentCandidate.id } },
        { cancelKey: "candidate:profile_autosave" }
      )
        .then(() => {
          formDirty.current = false;
          setSaveStatus("saved");
          if (saveStatusTimerRef.current) clearTimeout(saveStatusTimerRef.current);
          saveStatusTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2500);
          refreshCandidate();
        })
        .catch((err) => {
          // Superseded by a newer autosave, which carries the latest form.
          if (axios.isCancel(err)) return;
          setSaveStatus("error");
          enqueueSnackbar(f("toast.error"), { variant: "error" });
        });
    }, 1500);
    return () => clearTimeout(saveTimerRef.current);
  }, [form]);

  // Don't lose the last edits if the user leaves before the debounce fires:
  // flush the pending save on unmount and on page hide (keepalive survives
  // the page being torn down).
  const flushPendingSave = useCallback(() => {
    if (!formDirty.current || !currentCandidate?.id) return;
    formDirty.current = false;
    try {
      fetch("/candidate", {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": authenticityToken,
        },
        body: JSON.stringify({
          authenticity_token: authenticityToken,
          candidate: { ...formRef.current, id: currentCandidate.id },
        }),
      });
    } catch (e) { /* best effort */ }
  }, [authenticityToken, currentCandidate?.id]);

  useEffect(() => {
    window.addEventListener("pagehide", flushPendingSave);
    return () => {
      window.removeEventListener("pagehide", flushPendingSave);
      flushPendingSave();
    };
  }, [flushPendingSave]);

  // Refresh the candidate from the server, then show `successMsgId` as a toast
  // ONLY when the action did not raise profile completion. When completion goes
  // up, useProfileCompletionBoost shows the ProfileBoostSnackbar instead, and we
  // don't want a second (plain) snackbar stacking on top of it.
  const refreshCandidate = useCallback((successMsgId) => {
    const before = currentCandidate?.profile_completion ?? 0;
    replaceCurrentCandidate(dispatch, authenticityToken, (updated) => {
      if (!successMsgId) return;
      const after = updated?.profile_completion ?? before;
      if (after <= before) enqueueSnackbar(f(successMsgId), { variant: "success" });
    });
  }, [currentCandidate?.profile_completion, dispatch, authenticityToken, enqueueSnackbar, f]);

  const persistSkills = useCallback((hard, soft, lang) => {
    const skills = [
      ...hard.map((name) => ({ category: "hard", name, level: "" })),
      ...soft.map((name) => ({ category: "soft", name, level: "" })),
      ...lang.map((name) => ({ category: "lang", name, level: "" })),
    ];
    sh.post(`/update_skills/${currentCandidate.id}`, { authenticity_token: authenticityToken, skills })
      .then(() => {
        refreshCandidate();
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  }, [authenticityToken, dispatch, currentCandidate?.id, refreshCandidate, enqueueSnackbar, f]);

  const updateField = (key, value) => {
    formDirty.current = true;
    setSaveStatus("saving");
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // All destructive actions go through a confirm dialog — one mistap on a
  // 12px trash icon must not silently wipe a CV or an experience.
  const requestDelete = (run) => setConfirmDelete({ run });



  const desiredLocationsRef = useRef(desiredLocations);
  useEffect(() => { desiredLocationsRef.current = desiredLocations; }, [desiredLocations]);

  const saveDesiredLocations = useCallback((next) => {
    sh.put(
      "/candidate",
      { authenticity_token: authenticityToken, candidate: { desired_locations: next, id: currentCandidate.id } },
      { cancelKey: "candidate:desired_locations" }
    )
      .then(() => replaceCurrentCandidate(dispatch, authenticityToken, () => {}))
      .catch((err) => {
        // Superseded by a newer save that carries the full list.
        if (axios.isCancel(err)) return;
        enqueueSnackbar(f("toast.error"), { variant: "error" });
      });
  }, [authenticityToken, currentCandidate?.id, dispatch, enqueueSnackbar]);

  const handleDesiredLocationSelect = useCallback((place) => {
    if (!place.formatted_address && !place.name) return;
    const raw = place.name || place.formatted_address;
    const locationName = cleanLocation(raw);
    if (!locationName) return;
    const current = desiredLocationsRef.current;
    if (current.some((l) => l.label.toLowerCase() === locationName.toLowerCase())) return;
    const next = [...current, { label: locationName, ...regionFromPlace(place) }];
    setDesiredLocations(next);
    if (locationInputRef.current) locationInputRef.current.value = "";
    saveDesiredLocations(next);
  }, [saveDesiredLocations]);

  const handleRemoveDesiredLocation = (loc) => {
    const next = desiredLocations.filter((l) => l.label !== loc.label);
    setDesiredLocations(next);
    saveDesiredLocations(next);
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!sectionRequest) return;
    openSection(sectionRequest);
    if (onSectionRequestConsumed) onSectionRequestConsumed();
  }, [sectionRequest]);

  useEffect(() => {
    const serverUrl = currentCandidate?.portfolio_url ?? "";
    setForm((prev) => (prev.portfolio_url === serverUrl ? prev : { ...prev, portfolio_url: serverUrl }));
  }, [currentCandidate?.portfolio_url]);

  const openSection = (id) => {
    setExpandedSections((prev) => new Set(prev).add(id));
    // Wait for the section expand animation so the measured position is final.
    setTimeout(() => {
      const el = document.getElementById(`section-${id}`);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }, 250);
  };

  const completion = currentCandidate?.profile_completion || 0;

  const strengthColor =
    completion >= 90 ? "hsl(153, 68%, 25%)" :
      completion >= 70 ? "hsl(153, 68%, 40%)" :
        completion >= 40 ? "hsl(40, 90%, 50%)" :
          "hsl(0, 65%, 51%)";

  // Map backend missing_attributes (array of { attribute_name, weight }) to display items.
  const MISSING_ATTR_LABELS = {
    phone:                 { textId: "missing.phone_text", whyId: "missing.phone_why", field: "phone" },
    region:                { textId: "missing.region_text", whyId: "missing.region_why", section: "basic" },
    profile_picture_url:   { textId: "missing.profile_picture_url_text", whyId: "missing.profile_picture_url_why", section: "basic" },
    job_title:             { textId: "missing.job_title_text", whyId: "missing.job_title_why", section: "headline" },
    presentation:          { textId: "missing.presentation_text", whyId: "missing.presentation_why", section: "headline" },
    resume_url:            { textId: "missing.resume_url_text", whyId: "missing.resume_url_why", section: "documents" },
    experience_year:       { textId: "missing.experience_year_text", whyId: "missing.experience_year_why", section: "headline" },
    availability:          { textId: "missing.availability_text", whyId: "missing.availability_why", section: "preferences" },
    desired_sector:        { textId: "missing.desired_sector_text", whyId: "missing.desired_sector_why", section: "basic" },
    candidate_status:      { textId: "missing.candidate_status_text", whyId: "missing.candidate_status_why", section: "preferences" },
    portfolio_url:         { textId: "missing.portfolio_url_text", whyId: "missing.portfolio_url_why", section: "documents" },
    resource_skills_count: { textId: "missing.resource_skills_count_text", whyId: "missing.resource_skills_count_why", section: "skills" },
    experiences_count:     { textId: "missing.experiences_count_text", whyId: "missing.experiences_count_why", section: "experience" },
    trainings_count:       { textId: "missing.trainings_count_text", whyId: "missing.trainings_count_why", section: "education" },
    certificates_count:    { textId: "missing.certificates_count_text", whyId: "missing.certificates_count_why", section: "certifications" },
  };

  const missingItems = useMemo(() => {
    return (currentCandidate?.missing_attributes || [])
      .map((attr) => {
        const label = MISSING_ATTR_LABELS[attr.attribute_name];
        return label ? { ...label, weight: attr.weight } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 6);
  }, [currentCandidate?.missing_attributes]);

  // Mirrors Settings.jsx: region is derived from the Places result rather than
  // typed, so the value stays within the set the `regions.*` labels cover.
  const handleAddressSelect = (place) => {
    if (!place.address_components) return;

    let zipcode = "";
    let city = "";
    let adminLevel2 = "";
    let adminLevel1 = "";
    let country = "";

    place.address_components.forEach((c) => {
      if (c.types.includes("postal_code")) zipcode = c.long_name;
      if (c.types.includes("locality")) city = c.long_name;
      if (c.types.includes("administrative_area_level_2")) adminLevel2 = c.long_name?.toLowerCase()?.replace(/ /g, "_");
      if (c.types.includes("administrative_area_level_1")) adminLevel1 = c.long_name?.toLowerCase()?.replace(/ /g, "_");
      if (c.types.includes("country")) country = c.short_name.toLowerCase();
    });

    let region = "";
    if (country === "fr") {
      region = normalizeRegion(adminLevel1 || adminLevel2);
    } else if (country === "be") {
      region = adminLevel2 === "luxembourg" ? "be_luxembourg" : (adminLevel2 || adminLevel1);
    } else {
      region = adminLevel2 || adminLevel1;
    }

    formDirty.current = true;
    setSaveStatus("saving");
    setForm((prev) => ({ ...prev, region, city, zipcode, country }));
  };

  const handleSkillChange = (category, newValue) => {
    const ids = newValue.map((v) => v.id);
    const nextHard = category === "hard" ? ids : hardSkills;
    const nextSoft = category === "soft" ? ids : softSkills;
    const nextLang = category === "lang" ? ids : langSkills;
    if (category === "hard") setHardSkills(ids);
    else if (category === "soft") setSoftSkills(ids);
    else if (category === "lang") setLangSkills(ids);
    persistSkills(nextHard, nextSoft, nextLang);
  };

  const removeSkill = (list, setList, name) => {
    const next = list.filter((s) => s !== name);
    setList(next);
    const nextHard = setList === setHardSkills ? next : hardSkills;
    const nextSoft = setList === setSoftSkills ? next : softSkills;
    const nextLang = setList === setLangSkills ? next : langSkills;
    persistSkills(nextHard, nextSoft, nextLang);
  };

  const resetExpForm = () => {
    setExpForm({ title: "", company: "", location: "", start_date: "", end_date: "", current: false, description: "" });
    setEditingExpId(null);
    setShowExpForm(false);
    setExpTouched({});
    setExpSubmitAttempted(false);
  };

  const startEditExperience = (exp) => {
    setExpForm({
      title: exp.title || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || "",
      end_date: exp.end_date || "",
      current: !exp.end_date,
      description: exp.description || "",
    });
    setEditingExpId(exp.id);
    setShowExpForm(true);
    setExpTouched({});
    setExpSubmitAttempted(false);
  };

  const expDatesInvalid = Boolean(
    !expForm.current && expForm.start_date && expForm.end_date && expForm.end_date < expForm.start_date
  );
  const trainingDatesInvalid = Boolean(
    trainingForm.start_date && trainingForm.end_date && trainingForm.end_date < trainingForm.start_date
  );

  const saveErrorMessageId = (err, fallbackId) => {
    if (!err?.response) return "toast.network_error";
    if (err.response.status === 401) return "toast.session_expired";
    if (err.response.status === 404) return "toast.item_not_found";
    return fallbackId;
  };

  const expMissing = {
    title: !expForm.title?.trim(),
    company: !expForm.company?.trim(),
    location: !expForm.location?.trim(),
    start_date: !expForm.start_date,
    description: !expForm.description?.trim(),
  };
  const expHasMissing = Object.values(expMissing).some(Boolean);
  const expFieldInvalid = (field) => Boolean((expSubmitAttempted || expTouched[field]) && expMissing[field]);
  const touchExpField = (field) => setExpTouched((p) => ({ ...p, [field]: true }));

  const trainingMissing = {
    diploma: !trainingForm.diploma?.trim(),
    institution: !trainingForm.institution?.trim(),
    location: !trainingForm.location?.trim(),
    start_date: !trainingForm.start_date,
  };
  const trainingHasMissing = Object.values(trainingMissing).some(Boolean);
  const trainingFieldInvalid = (field) => Boolean((trainingSubmitAttempted || trainingTouched[field]) && trainingMissing[field]);
  const touchTrainingField = (field) => setTrainingTouched((p) => ({ ...p, [field]: true }));

  const handleSaveExperience = () => {
    if (savingExp) return;
    if (expHasMissing || expDatesInvalid) {
      setExpSubmitAttempted(true);
      return;
    }
    setSavingExp(true);
    const { current, ...expData } = expForm;
    const payload = {
      authenticity_token: authenticityToken,
      experience: { ...expData, end_date: current ? null : expData.end_date, candidate_id: currentCandidate.id },
    };
    const request = editingExpId ? sh.put(`experience/${editingExpId}`, payload) : sh.post("experience", payload);
    request
      .then(() => {
        resetExpForm();
        refreshCandidate(editingExpId ? "toast.experience_updated" : "toast.experience_added");
      })
      .catch((err) => {
        const messageId = saveErrorMessageId(err, "toast.experience_save_error");
        if (messageId === "toast.item_not_found") {
          resetExpForm();
          refreshCandidate();
        }
        enqueueSnackbar(f(messageId), { variant: "error" });
      })
      .finally(() => setSavingExp(false));
  };

  const handleDeleteExperience = (id) => {
    sh.delete(`experience/${id}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        if (editingExpId === id) resetExpForm();
        refreshCandidate("toast.experience_deleted");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const resetTrainingForm = () => {
    setTrainingForm({ diploma: "", institution: "", study_field: "", location: "", start_date: "", end_date: "" });
    setEditingTrainingId(null);
    setShowTrainingForm(false);
    setTrainingTouched({});
    setTrainingSubmitAttempted(false);
  };

  const startEditTraining = (t) => {
    setTrainingForm({
      diploma: t.diploma || "",
      institution: t.institution || "",
      study_field: t.study_field || "",
      location: t.location || "",
      start_date: t.start_date || "",
      end_date: t.end_date || "",
    });
    setEditingTrainingId(t.id);
    setShowTrainingForm(true);
    setTrainingTouched({});
    setTrainingSubmitAttempted(false);
  };

  const handleSaveTraining = () => {
    if (savingTraining) return;
    if (trainingHasMissing || trainingDatesInvalid) {
      setTrainingSubmitAttempted(true);
      return;
    }
    setSavingTraining(true);
    const payload = { authenticity_token: authenticityToken, training: { ...trainingForm, candidate_id: currentCandidate.id } };
    const request = editingTrainingId ? sh.put(`training/${editingTrainingId}`, payload) : sh.post("training", payload);
    request
      .then(() => {
        resetTrainingForm();
        refreshCandidate(editingTrainingId ? "toast.training_updated" : "toast.training_added");
      })
      .catch((err) => {
        const messageId = saveErrorMessageId(err, "toast.training_save_error");
        if (messageId === "toast.item_not_found") {
          resetTrainingForm();
          refreshCandidate();
        }
        enqueueSnackbar(f(messageId), { variant: "error" });
      })
      .finally(() => setSavingTraining(false));
  };

  const handleDeleteTraining = (id) => {
    sh.delete(`training/${id}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        if (editingTrainingId === id) resetTrainingForm();
        refreshCandidate("toast.training_deleted");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const handleAddCertificate = (option) => {
    if (savingCertificate) return;
    if (certificates.some((c) => c.name === option.id && c.category === option.category)) return;
    setSavingCertificate(true);
    sh.post("certificate", {
      authenticity_token: authenticityToken,
      certificate: { name: option.id, category: option.category },
    })
      .then(() => {
        refreshCandidate();
      })
      .catch(() => {
        enqueueSnackbar(f("toast.error"), { variant: "error" });
      })
      .finally(() => setSavingCertificate(false));
  };

  const handleDeleteCertificate = (id) => {
    sh.delete(`certificate/${id}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        refreshCandidate();
      })
      .catch(() => {
        enqueueSnackbar(f("toast.error"), { variant: "error" });
      });
  };

  const clearPendingPhotos = () => {
    setPendingProjectPhotos((prev) => {
      prev.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      return [];
    });
  };

  const handleRemovePendingPhoto = (uid) => {
    setPendingProjectPhotos((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.uid !== uid);
    });
  };

  const handleUploadProjectPhoto = async (files) => {
    const fileList = Array.from(files || []);
    if (!fileList.length) return;

    for (const file of fileList) {
      // Show the thumbnail immediately, before the upload starts.
      const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      setPendingProjectPhotos((prev) => [
        ...prev,
        { uid, previewUrl, progress: 0, status: "uploading", blobSignedId: null },
      ]);

      try {
        const checksum = await fileChecksum(file);
        const presignedRes = await fetch("/presigned_url", {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ file: { filename: file.name, byte_size: file.size, checksum, content_type: file.type, metadata: { folder: "project_photos" } } }),
        });
        if (!presignedRes.ok) throw new Error();
        const presigned = await presignedRes.json();
        await uploadFileWithProgress(
          presigned.direct_upload.url,
          presigned.direct_upload.headers,
          file,
          (progress) =>
            setPendingProjectPhotos((prev) => prev.map((p) => (p.uid === uid ? { ...p, progress } : p))),
        );
        setPendingProjectPhotos((prev) =>
          prev.map((p) =>
            p.uid === uid ? { ...p, progress: 100, status: "done", blobSignedId: presigned.blob_signed_id } : p,
          ),
        );
      } catch {
        setPendingProjectPhotos((prev) => prev.map((p) => (p.uid === uid ? { ...p, status: "error" } : p)));
        enqueueSnackbar(f("toast.upload_error"), { variant: "error" });
      }
    }

    if (projectPhotoInputRef.current) projectPhotoInputRef.current.value = "";
  };

  const renderPendingPhotoThumbs = () => {
    if (pendingProjectPhotos.length === 0) return null;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "4px" }}>
        {pendingProjectPhotos.map((photo) => (
          <Box key={photo.uid} sx={{ position: "relative", width: 72, height: 72 }}>
            <Box
              component="img"
              src={photo.previewUrl}
              alt=""
              sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: "8px", border: "1px solid hsla(178,93%,10%,0.12)", opacity: photo.status === "done" ? 1 : 0.55 }}
            />
            {photo.status !== "done" && (
              <Box sx={{ position: "absolute", inset: 0, borderRadius: "8px", bgcolor: "hsla(178,93%,10%,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {photo.status === "error" ? (
                  <AlertCircle size={18} color="#fff" />
                ) : (
                  <Typography sx={{ color: "#fff", fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
                    {photo.progress}%
                  </Typography>
                )}
              </Box>
            )}
            {photo.status === "uploading" && (
              <Box sx={{ position: "absolute", left: 4, right: 4, bottom: 4, height: 3, borderRadius: "2px", bgcolor: "hsla(0,0%,100%,0.4)", overflow: "hidden" }}>
                <Box sx={{ height: "100%", width: `${photo.progress}%`, bgcolor: "#fff", transition: "width 0.15s" }} />
              </Box>
            )}
            <Box
              component="button"
              onClick={() => handleRemovePendingPhoto(photo.uid)}
              sx={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", bgcolor: "hsl(0,65%,51%)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", p: 0, color: "#fff" }}
            >
              <X size={10} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const handleAddProject = () => {
    setSavingProject(true);
    sh.post("/candidate_projects", {
      authenticity_token: authenticityToken,
      candidate_project: { ...projectForm, candidate_id: currentCandidate.id },
      photos: pendingProjectPhotos.filter((p) => p.status === "done").map((p) => p.blobSignedId),
    })
      .then(() => {
        setProjectForm({ title: "", project_type: "", location: "", end_date: "", description: "" });
        clearPendingPhotos();
        setShowProjectForm(false);
        refreshCandidate("toast.project_added");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }))
      .finally(() => setSavingProject(false));
  };

  const handleUpdateProject = (id) => {
    setSavingProject(true);
    sh.put(`/candidate_projects/${id}`, {
      authenticity_token: authenticityToken,
      candidate_project: { ...projectForm },
      photos: pendingProjectPhotos.filter((p) => p.status === "done").map((p) => p.blobSignedId),
    })
      .then(() => {
        setProjectForm({ title: "", project_type: "", location: "", end_date: "", description: "" });
        clearPendingPhotos();
        setEditingProjectId(null);
        refreshCandidate("toast.project_updated");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }))
      .finally(() => setSavingProject(false));
  };

  const handleDeleteProject = (id) => {
    sh.delete(`/candidate_projects/${id}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        refreshCandidate("toast.project_deleted");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const handleDeleteProjectPhoto = (projectId, signedId) => {
    sh.delete(`/candidate_projects/${projectId}/photos/${signedId}`, { data: { authenticity_token: authenticityToken } })
      .then(() => {
        replaceCurrentCandidate(dispatch, authenticityToken, () => {});
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const handleToggleProjectsSharing = (nextPublic) => {
    setTogglingShare(true);
    sh.patch("/candidate/projects_sharing", { authenticity_token: authenticityToken, projects_public: nextPublic })
      .then(() => {
        refreshCandidate(nextPublic ? "toast.projects_shared" : "toast.projects_unshared");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }))
      .finally(() => setTogglingShare(false));
  };

  const handleCopyProjectsLink = async () => {
    const url = publicProjectsUrl;
    if (!url) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      ta.remove();
    }
    if (ok) {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } else {
      enqueueSnackbar(f("toast.error"), { variant: "error" });
    }
  };

  // Portfolio upload
  const handlePortfolioFile = async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    setUploadingPortfolio(true);
    try {
      const presigned = await uploadPortfolio(file);
      await attachPortfolioToCandidate({
        id: currentCandidate.id,
        authenticity_token: authenticityToken,
        blob_signed_id: presigned.blob_signed_id,
      });
      refreshCandidate("toast.portfolio_uploaded");
    } catch {
      enqueueSnackbar(f("toast.upload_error"), { variant: "error" });
    } finally {
      setUploadingPortfolio(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const presigned = await directUpload(file, "profile_pictures", file.type);
      await sh.put("/candidate/upload_picture", {
        authenticity_token: authenticityToken,
        candidate: { id: currentCandidate.id, picture: presigned.blob_signed_id, attribute_name: "profile_picture" },
      });
      refreshCandidate("toast.photo_updated");
    } catch {
      enqueueSnackbar(f("toast.upload_error"), { variant: "error" });
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = () => {
    sh.put("/purge_attachment", {
      authenticity_token: authenticityToken,
      candidate: { id: currentCandidate.id },
      attachment: { field_name: "profile_picture" },
    })
      .then(() => {
        refreshCandidate("toast.photo_deleted");
      })
      .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const handleDeletePortfolio = () => {    sh.put("/purge_attachment", {
    authenticity_token: authenticityToken,
    candidate: { id: currentCandidate.id },
    attachment: { field_name: "portfolio_file" },
  })
    .then(() => sh.put(
      "/candidate",
      { authenticity_token: authenticityToken, candidate: { id: currentCandidate.id, portfolio_url: "" } },
      { cancelKey: "candidate:portfolio_url" }
    ))
    .then(() => {
      updateField("portfolio_url", "");
      refreshCandidate("toast.portfolio_deleted");
    })
    .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
  };

  const experiences = currentCandidate?.experiences || [];
  const trainings = currentCandidate?.trainings || [];
  const certificates = currentCandidate?.certificates || [];
  const publicProjectsUrl = useMemo(() => {
    const url = currentCandidate?.public_projects_url;
    if (!url || typeof window === "undefined") return url;
    try {
      const { pathname } = new URL(url);
      return `${window.location.origin}${pathname}`;
    } catch (e) {
      return url;
    }
  }, [currentCandidate?.public_projects_url]);
  const projects = currentCandidate?.candidate_projects || [];
  const projectPhotosUploading = pendingProjectPhotos.some((p) => p.status === "uploading");

  const dateLocale = { fr: "fr-BE", "fr-fr": "fr-FR", nl: "nl-BE", en: "en-GB" }[i18nLocale] || "fr-BE";
  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString(dateLocale, { month: "short", year: "numeric" });
  };

  if (!currentCandidate) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* ── CV Parser Modal (commented out — UI ready) ──
      <Dialog open={showCvModal} onClose={() => setShowCvModal(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "hsla(153, 68%, 25%, 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Zap size={20} color="hsl(153, 68%, 25%)" />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "16px", color: "hsl(178, 93%, 10%)" }}>
                Importez votre CV en un clic
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "2px" }}>
                Notre IA analyse automatiquement votre CV et remplit votre profil pour vous.
              </Typography>
            </Box>
          </Box>
          {[
            "Uploadez votre CV (PDF ou DOCX)",
            "L'IA extrait vos informations automatiquement",
            "Vérifiez et validez votre profil complet",
          ].map((step, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: "hsla(160, 10%, 96%, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", flexShrink: 0 }}>
                {i + 1}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                  {step}
                </Typography>
              </Box>
            </Box>
          ))}
          <Box component="button" onClick={() => setShowCvModal(false)} sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", py: "12px", borderRadius: "12px", border: "none", cursor: "pointer", bgcolor: "hsl(153, 68%, 25%)", color: "hsl(0, 0%, 100%)", fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", "&:hover": { bgcolor: "hsl(153, 68%, 30%)" }, transition: "background 0.15s" }}>
            <Upload size={16} />
            Uploader mon CV maintenant
          </Box>
          <Box component="button" onClick={() => setShowCvModal(false)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", textAlign: "center", "&:hover": { color: "hsl(178, 93%, 10%)" }, transition: "color 0.15s" }}>
            Je préfère remplir manuellement
          </Box>
        </Box>
      </Dialog>
      ── end CV Parser Modal ── */}

      {/* Mobile view toggle */}
      <Box sx={{ display: { xs: "flex", lg: "none" }, p: "4px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.5)", border: "1px solid hsla(160, 10%, 90%, 0.6)" }}>
        {[
          { key: "edit", icon: User, label: f("my_profile") },
          { key: "recruiter", icon: Eye, label: f("recruiter_view") },
        ].map((v) => (
          <Box
            key={v.key}
            component="button"
            onClick={() => setMobileView(v.key)}
            sx={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              py: "8px", borderRadius: "8px", border: "none", cursor: "pointer",
              fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
              bgcolor: mobileView === v.key ? "hsl(0, 0%, 100%)" : "transparent",
              color: mobileView === v.key ? "hsl(178, 93%, 10%)" : "hsl(178, 10%, 45%)",
              boxShadow: mobileView === v.key ? "0 1px 3px hsla(0,0%,0%,0.06)" : "none",
              transition: "all 0.15s",
            }}
          >
            <v.icon size={13} /> {v.label}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" }, gap: "24px" }}>

        {/* Edit sections */}
        <Box sx={{ display: mobileView === "recruiter" ? { xs: "none", lg: "flex" } : "flex", flexDirection: "column", gap: "16px" }}>

          {/* Completion summary */}
          <Box sx={{ ...cardSx, p: "20px" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "12px" }}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "20px", color: "hsl(178, 93%, 10%)" }}>{f("my_profile")}</Typography>
                  {saveStatus !== "idle" && (
                    <Box sx={{
                      display: "inline-flex", alignItems: "center", gap: "5px",
                      px: "10px", py: "3px", borderRadius: "9999px",
                      fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      bgcolor: saveStatus === "error" ? "hsla(0, 65%, 51%, 0.1)" : "hsla(153, 68%, 25%, 0.1)",
                      color: saveStatus === "error" ? "hsl(0, 65%, 51%)" : "hsl(153, 68%, 25%)",
                    }}>
                      {saveStatus === "saving" && <CircularProgress size={10} sx={{ color: "inherit" }} />}
                      {saveStatus === "saved" && <CheckCircle size={12} />}
                      {saveStatus === "error" && <AlertCircle size={12} />}
                      {f(`autosave_${saveStatus}`)}
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>{f("profile_subtitle")}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography sx={{ fontSize: "24px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: strengthColor, lineHeight: 1, mb: "4px" }}>
                  {completion}%
                </Typography>
                <Typography sx={{ fontSize: "10px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>{f("completed")}</Typography>
              </Box>
            </Box>
            <Box sx={{ width: "100%", height: "10px", bgcolor: "hsla(160, 10%, 96%, 0.5)", borderRadius: "9999px", overflow: "hidden" }}>
              <Box
                component={motion.div}
                initial={{ width: 0 }}
                animate={{ width: `${completion}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                sx={{ height: "10px", borderRadius: "9999px", background: `linear-gradient(90deg, ${strengthColor}, ${strengthColor})` }}
              />
            </Box>
          </Box>

          {/* Improvement suggestions */}
          {missingItems.length > 0 && (
            <Box sx={{ ...cardSx, p: "20px" }}>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px", color: "hsl(178, 93%, 10%)", mb: "12px", display: "flex", alignItems: "center", gap: "8px" }}>{f("suggested_improvements")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {(showAllSuggestions ? missingItems : missingItems.slice(0, 2)).map((item) => (
                  <React.Fragment key={item.textId}>
                    <Box
                      component="button"
                      onClick={() => {
                        if (item.field) {
                          setQuickFixField(quickFixField === item.field ? null : item.field);
                        } else {
                          openSection(item.section);
                        }
                      }}
                      sx={{
                        display: "flex", alignItems: "flex-start", gap: "12px",
                        p: "12px", borderRadius: "12px",
                        bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "none",
                        cursor: "pointer", textAlign: "left", width: "100%",
                        "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.6)" },
                        transition: "background 0.15s",
                      }}
                    >
                      <AlertCircle size={14} color="hsl(40, 90%, 50%)" style={{ flexShrink: 0, marginTop: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                          {f(item.textId)}
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                          {f(item.whyId)}
                        </Typography>
                      </Box>
                      <Box sx={{ px: "8px", py: "3px", borderRadius: "9999px", bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)", fontSize: "10px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", flexShrink: 0, alignSelf: "center" }}>
                        {f("points_suffix", { weight: item.weight })}
                      </Box>
                    </Box>
                    {item.field && quickFixField === item.field && (
                      <TextField
                        value={form[item.field] || ""}
                        onChange={(e) => updateField(item.field, e.target.value)}
                        type={item.field === "phone" ? "tel" : "text"}
                        autoFocus
                        fullWidth
                        size="small"
                        sx={textFieldSx}
                      />
                    )}
                  </React.Fragment>
                ))}
                {missingItems.length > 2 && (
                  <Box
                    component="button"
                    onClick={() => setShowAllSuggestions((prev) => !prev)}
                    sx={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                      border: "none", bgcolor: "transparent", cursor: "pointer",
                      fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      color: "hsl(178, 10%, 45%)", py: "4px",
                      "&:hover": { color: "hsl(153, 68%, 25%)" },
                      transition: "color 0.15s",
                    }}
                  >
                    {showAllSuggestions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    {showAllSuggestions ? f("see_less") : f("see_more_count", { count: Math.min(missingItems.length, 6) - 2 })}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* CV & Documents */}
          <Section title={f("section_documents")} id="documents" icon={FileText} expanded={expandedSections.has("documents")} onToggle={toggleSection} sx>
            <Typography sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px" }}>{f("cv_label")}</Typography>
            <FileDropzone
              key={currentCandidate.resume_url || "no-resume"}
              height="80px"
              handleFileInput={async (files) => {
                const file = Array.isArray(files) ? files[0] : files;
                if (!file) return;
                try {
                  const presigned = await uploadResume(file);
                  await sh.put("/upload_resume", {
                    authenticity_token: authenticityToken,
                    candidate: { id: currentCandidate.id, resume: presigned.blob_signed_id },
                  });
                  refreshCandidate("toast.cv_updated");
                } catch {
                  enqueueSnackbar(f("toast.upload_error"), { variant: "error" });
                }
              }}
              filesLimit={1}
              initialFiles={currentCandidate.resume_url ? [currentCandidate.resume_url] : []}
              fileName={currentCandidate.resume_url ? decodeURIComponent(currentCandidate.resume_url.split("/").pop().split("?")[0]) : ""}
              deleteJustImportedFile={() => requestDelete(() => {
                sh.put("/purge_attachment", {
                  authenticity_token: authenticityToken,
                  candidate: { id: currentCandidate.id },
                  attachment: { field_name: "resume" },
                })
                  .then(() => {
                    refreshCandidate("toast.cv_deleted");
                  })
                  .catch(() => enqueueSnackbar(f("toast.error"), { variant: "error" }));
              })}
              type="candidate"
            />

            <Box sx={{ my: "16px", borderTop: "1px solid hsla(160, 10%, 90%, 0.6)" }} />

            <Typography sx={{ fontSize: "11px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "6px" }}>{f("portfolio_label")}</Typography>
            {currentCandidate.portfolio_file_url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)" }}>
                <FileText size={16} color="hsl(153, 68%, 25%)" style={{ flexShrink: 0 }} />
                <Typography sx={{ flex: 1, fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {decodeURIComponent((currentCandidate.portfolio_file_url || "").split("/").pop().split("?")[0]) || f("portfolio_label")}
                </Typography>
                <Box component="button" type="button" onClick={() => requestDelete(handleDeletePortfolio)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "hsl(178, 10%, 45%)", "&:hover": { color: "hsl(0, 65%, 51%)" }, transition: "color 0.15s" }}>
                  <Trash2 size={14} />
                </Box>
              </Box>
            ) : form.portfolio_url ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)", border: "1px solid hsla(160, 10%, 90%, 0.4)" }}>
                <LinkIcon size={16} color="hsl(153, 68%, 25%)" style={{ flexShrink: 0 }} />
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: "12px", cursor: "pointer", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} 
                  onClick={() => window.open(form.portfolio_url, "_blank")} 
                >
                  {form.portfolio_url.length > 100 ? `${form.portfolio_url.slice(0, 100)}…` : form.portfolio_url}
                </Typography>
                <Box component="button" type="button" onClick={() => requestDelete(() => updateField("portfolio_url", ""))} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "hsl(178, 10%, 45%)", "&:hover": { color: "hsl(0, 65%, 51%)" }, transition: "color 0.15s" }}>
                  <Trash2 size={14} />
                </Box>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography component="label" sx={labelSx}>{f("portfolio_url_label")}</Typography>
                  <TextField value={form.portfolio_url || ""} onChange={(e) => updateField("portfolio_url", e.target.value)} placeholder="https://..." fullWidth size="small" sx={textFieldSx} />
                </Box>
                <Typography sx={{ fontSize: "10px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", my: "8px", textAlign: "center" }}>{f("or_separator")}</Typography>
                <FileDropzone
                  height="60px"
                  handleFileInput={handlePortfolioFile}
                  filesLimit={1}
                  initialFiles={[]}
                  deleteJustImportedFile={() => {}}
                  type="candidate"
                />
                {uploadingPortfolio && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "8px" }}>
                    <CircularProgress size={12} sx={{ color: "hsl(153, 68%, 25%)" }} />
                    <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>{f("uploading")}</Typography>
                  </Box>
                )}
              </>
            )}
          </Section>

          {/* Basic info */}
          <Section title={f("section_basic")} id="basic" icon={User} expanded={expandedSections.has("basic")} onToggle={toggleSection}>
            {/* Profile picture */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "16px", mb: "20px", pb: "20px", borderBottom: "1px solid hsla(160, 10%, 90%, 0.6)" }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  sx={{
                    width: "72px", height: "72px", borderRadius: "9999px", overflow: "hidden",
                    bgcolor: "hsla(153, 68%, 25%, 0.1)",
                    border: "2px solid hsla(160, 10%, 90%, 0.8)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", p: 0, position: "relative",
                    "&:hover .photo-overlay": { opacity: 1 },
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.5)" },
                  }}
                >
                  {currentCandidate.profile_picture_url ? (
                    <Box component="img" src={currentCandidate.profile_picture_url} alt="Photo de profil" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Typography sx={{ fontSize: "22px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                      {(currentCandidate.first_name?.[0] || "?")}{(currentCandidate.last_name?.[0] || "")}
                    </Typography>
                  )}
                  <Box className="photo-overlay" sx={{
                    position: "absolute", inset: 0, borderRadius: "9999px",
                    bgcolor: "hsla(0, 0%, 0%, 0.45)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: 0, transition: "opacity 0.15s",
                  }}>
                    <Camera size={18} color="hsl(0, 0%, 100%)" />
                  </Box>
                </Box>
                {currentCandidate.profile_picture_url && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => requestDelete(handleDeletePhoto)}
                    sx={{
                      position: "absolute", top: "-2px", right: "-2px",
                      width: "20px", height: "20px", borderRadius: "9999px",
                      bgcolor: "hsl(0, 65%, 51%)", color: "hsl(0, 0%, 100%)",
                      border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 1px 3px hsla(0,0%,0%,0.2)",
                      "&:hover": { bgcolor: "hsl(0, 65%, 45%)" },
                      transition: "background 0.15s",
                    }}
                  >
                    <X size={11} />
                  </Box>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)", mb: "4px" }}>{f("photo_label")}</Typography>
                <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mb: "8px" }}>{f("photo_formats")}</Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    px: "12px", py: "7px", borderRadius: "10px",
                    border: "1px solid hsla(153, 68%, 25%, 0.3)",
                    bgcolor: "hsl(153, 68%, 25%)",
                    color: "hsl(0, 0%, 100%)",
                    fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    cursor: uploadingPhoto ? "not-allowed" : "pointer",
                    "&:hover": { bgcolor: "hsl(153, 68%, 30%)" },
                    transition: "background 0.15s",
                  }}
                >
                  {uploadingPhoto ? (
                    <CircularProgress size={10} sx={{ color: "hsl(0, 0%, 100%)" }} />
                  ) : (
                    <Upload size={11} />
                  )}
                  {uploadingPhoto ? f("btn_uploading") : (currentCandidate.profile_picture_url ? f("photo_change") : f("photo_add"))}
                </Box>
                <Box
                  component="input"
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handlePhotoUpload}
                  sx={{ display: "none" }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
              <Box>
                <Typography component="label" sx={labelSx}>{f("highest_degree")}</Typography>
                <CustomSelect value={form.highest_degree} onChange={(v) => updateField("highest_degree", v)} options={degreeOptions} />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>{f("desired_sector")}</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {sectorOptions.map((opt) => {
                    const current = form.desired_sector || [];
                    const isSelected = current.includes(opt.value);
                    return (
                      <Box
                        key={opt.value}
                        component="button"
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => updateField(
                          "desired_sector",
                          isSelected ? current.filter((v) => v !== opt.value) : [...current, opt.value]
                        )}
                        sx={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          px: "12px", py: "6px", borderRadius: "9999px", cursor: "pointer",
                          border: isSelected ? "1px solid hsl(153, 68%, 25%)" : "1px solid hsla(160, 10%, 90%, 0.6)",
                          bgcolor: isSelected ? "hsl(153, 68%, 25%)" : "hsl(0, 0%, 100%)",
                          color: isSelected ? "hsl(0, 0%, 100%)" : "hsl(178, 93%, 10%)",
                          fontSize: "12px",
                          fontFamily: isSelected ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                          transition: "background-color 0.15s, border-color 0.15s",
                          "&:hover": { borderColor: "hsl(153, 68%, 25%)" },
                        }}
                      >
                        {isSelected && <CheckCircle size={12} />}
                        {opt.label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              
            </Box>
            <Box sx={{ mt: "16px" }}>
              <Typography component="label" sx={labelSx}>{f("region_label")}</Typography>
              {form.region && (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "12px", py: "6px", mb: "10px", borderRadius: "9999px", bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)", fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
                  <MapPin size={10} />
                  {intl.formatMessage({ id: `regions.${form.region}`, defaultMessage: form.region })}
                  <Box
                    component="button"
                    type="button"
                    aria-label={f("remove_region")}
                    onClick={() => updateField("region", "")}
                    sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}
                  >
                    <X size={10} />
                  </Box>
                </Box>
              )}
              <Autocomplete
                apiKey={process.env.GOOGLE_AUTOCOMPLETE_API_KEY}
                language={i18nLocale === "nl" ? "nl" : i18nLocale === "en" ? "en" : "fr"}
                onPlaceSelected={handleAddressSelect}
                options={{ types: ["geocode"], componentRestrictions: { country: ["be", "lu", "fr"] } }}
                style={{
                  width: "100%", height: "44px", borderRadius: "12px",
                  border: "1px solid hsla(160, 10%, 90%, 0.6)",
                  backgroundColor: "hsla(160, 10%, 96%, 0.3)",
                  padding: "0 12px", fontSize: "13px",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)", outline: "none",
                  boxSizing: "border-box",
                }}
                placeholder={f("region_search_placeholder")}
              />
            </Box>
            <Box sx={{ mt: "16px" }}>
              <Typography component="label" sx={labelSx}>{f("desired_regions")}</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: desiredLocations.length ? "10px" : 0 }}>
                {desiredLocations.map((loc) => (
                  <Box key={loc.label} sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "12px", py: "6px", borderRadius: "9999px", bgcolor: "hsla(153, 68%, 25%, 0.1)", color: "hsl(153, 68%, 25%)", fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
                    <MapPin size={10} /> {loc.label}
                    <Box component="button" type="button" onClick={() => handleRemoveDesiredLocation(loc)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}>
                      <X size={10} />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Autocomplete
                ref={locationInputRef}
                apiKey={process.env.GOOGLE_AUTOCOMPLETE_API_KEY}
                language={i18nLocale === "nl" ? "nl" : i18nLocale === "en" ? "en" : "fr"}
                onPlaceSelected={handleDesiredLocationSelect}
                options={{ types: ["geocode"], componentRestrictions: { country: ["be", "lu", "fr"] } }}
                style={{
                  width: "100%", height: "44px", borderRadius: "12px",
                  border: "1px solid hsla(160, 10%, 90%, 0.6)",
                  backgroundColor: "hsla(160, 10%, 96%, 0.3)",
                  padding: "0 12px", fontSize: "13px",
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: "hsl(178, 93%, 10%)", outline: "none",
                  boxSizing: "border-box",
                }}
                placeholder={f("location_search_placeholder")}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1", mt: "16px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => updateField("work_permit", !(form.work_permit ?? true))}
                sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, textAlign: "left" }}
              >
                <Box sx={{ position: "relative", width: "36px", height: "20px", borderRadius: "9999px", flexShrink: 0, bgcolor: (form.work_permit ?? true) ? "hsl(153, 68%, 25%)" : "hsla(160, 10%, 90%, 1)", transition: "background-color 0.2s" }}>
                  <Box sx={{ position: "absolute", top: "2px", left: (form.work_permit ?? true) ? "18px" : "2px", width: "16px", height: "16px", borderRadius: "9999px", bgcolor: "hsl(0, 0%, 100%)", boxShadow: "0 1px 3px hsla(0,0%,0%,0.2)", transition: "left 0.2s" }} />
                </Box>
                <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                  {f("work_permit")}
                </Typography>
              </Box>
            </Box>
          </Section>

          {/* Title & summary */}
          <Section title={f("section_headline")} id="headline" icon={Target} expanded={expandedSections.has("headline")} onToggle={toggleSection}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
              <Box>
                <Typography component="label" sx={labelSx}>{f("job_title")}</Typography>
                <TextField value={form.job_title || ""} onChange={(e) => updateField("job_title", e.target.value)} fullWidth size="small" sx={textFieldSx} />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>{f("years_experience")}</Typography>
                <Box
                  component="input"
                  type="number"
                  min={0}
                  value={form.experience_year ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    // Keep "" as "" — coercing it to 0 here refills the field
                    // the instant it's cleared, making it impossible to delete.
                    if (raw === "") {
                      updateField("experience_year", "");
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    updateField("experience_year", Number.isNaN(parsed) ? "" : Math.max(0, parsed));
                  }}
                  sx={inputSx}
                />
              </Box>
            </Box>
            <Box sx={{ mt: "12px" }}>
              <Typography component="label" sx={labelSx}>{f("about_me")}</Typography>
              <Box
                component="textarea"
                value={form.presentation}
                onChange={(e) => updateField("presentation", e.target.value)}
                placeholder={f("about_placeholder")}
                rows={4}
                sx={{
                  ...inputSx,
                  height: "auto",
                  minHeight: "100px",
                  py: "10px",
                  resize: "none",
                  "&::placeholder": { color: "hsl(178, 10%, 45%)", opacity: 0.5 },
                }}
              />
            </Box>
          </Section>

          {/* Preferences */}
          <Section title={f("section_preferences")} id="preferences" icon={Shield} expanded={expandedSections.has("preferences")} onToggle={toggleSection}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
              <Box>
                <Typography component="label" sx={labelSx}>{f("availability_label")}</Typography>
                <CustomSelect value={form.availability} onChange={(v) => updateField("availability", v)} options={availabilityOptions} />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>{f("desired_job")}</Typography>
                <TextField value={form.desired_job_title || ""} onChange={(e) => updateField("desired_job_title", e.target.value)} fullWidth size="small" sx={textFieldSx} />
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography component="label" sx={labelSx}>{f("status_label")}</Typography>
                <CustomSelect value={form.candidate_status} onChange={(v) => updateField("candidate_status", v)} options={statusOptions} />
              </Box>
            </Box>
          </Section>

          {/* Experience */}
          <Section title={f("section_experience")} id="experience" icon={Briefcase} expanded={expandedSections.has("experience")} onToggle={toggleSection}>
            {experiences.map((exp) => {
              // The edit form below stands in for the card being edited.
              if (showExpForm && editingExpId === exp.id) return null;
              return (
                <Box key={exp.id} sx={{ p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)", mb: "8px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px", color: "hsl(178, 93%, 10%)" }}>
                      {exp.title}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                      {exp.company} · {formatDate(exp.start_date)} — {exp.end_date ? formatDate(exp.end_date) : f("common.present")}
                    </Typography>
                    {exp.location && (
                      <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", display: "flex", alignItems: "center", gap: "4px", mt: "2px" }}>
                        <MapPin size={10} /> {exp.location}
                      </Typography>
                    )}
                    {exp.description && (
                      <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "600px" }}>
                        {exp.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                    <Box component="button" onClick={() => startEditExperience(exp)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(178, 93%, 10%)", "&:hover": { color: "hsl(178, 60%, 30%)" } }}>
                      <Pencil size={12} />
                    </Box>
                    <Box component="button" onClick={() => requestDelete(() => handleDeleteExperience(exp.id))} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(0, 65%, 51%)", "&:hover": { color: "hsl(0, 65%, 40%)" } }}>
                      <Trash2 size={12} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {showExpForm ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "12px", borderRadius: "12px", border: "1px solid hsla(160, 10%, 90%, 0.6)" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "8px" }}>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("exp_title")} *</Typography>
                    <TextField value={expForm.title || ""} onChange={(e) => setExpForm((p) => ({ ...p, title: e.target.value }))} onBlur={() => touchExpField("title")} fullWidth size="small" sx={expFieldInvalid("title") ? textFieldErrorSx : textFieldSx} />
                    {expFieldInvalid("title") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("company")} *</Typography>
                    <TextField value={expForm.company || ""} onChange={(e) => setExpForm((p) => ({ ...p, company: e.target.value }))} onBlur={() => touchExpField("company")} fullWidth size="small" sx={expFieldInvalid("company") ? textFieldErrorSx : textFieldSx} />
                    {expFieldInvalid("company") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.location")} *</Typography>
                    <TextField value={expForm.location || ""} onChange={(e) => setExpForm((p) => ({ ...p, location: e.target.value }))} onBlur={() => touchExpField("location")} fullWidth size="small" sx={expFieldInvalid("location") ? textFieldErrorSx : textFieldSx} />
                    {expFieldInvalid("location") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", pt: "23px" }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setExpForm((p) => ({ ...p, current: !p.current, end_date: !p.current ? "" : p.end_date }))}
                      sx={{ display: "flex", alignItems: "center", gap: "10px", border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, textAlign: "left" }}
                    >
                      <Box sx={{ position: "relative", width: "36px", height: "20px", borderRadius: "9999px", flexShrink: 0, bgcolor: expForm.current ? "hsl(153, 68%, 25%)" : "hsla(160, 10%, 90%, 1)", transition: "background-color 0.2s" }}>
                        <Box sx={{ position: "absolute", top: "2px", left: expForm.current ? "18px" : "2px", width: "16px", height: "16px", borderRadius: "9999px", bgcolor: "hsl(0, 0%, 100%)", boxShadow: "0 1px 3px hsla(0,0%,0%,0.2)", transition: "left 0.2s" }} />
                      </Box>
                      <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                        {f("current_position")}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.start_date")} *</Typography>
                    <Box component="input" type="date" value={expForm.start_date} onChange={(e) => setExpForm((p) => ({ ...p, start_date: e.target.value }))} onBlur={() => touchExpField("start_date")} sx={expFieldInvalid("start_date") ? inputErrorSx : inputSx} />
                    {expFieldInvalid("start_date") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.end_date")}</Typography>
                    <Box component="input" type="date" value={expForm.end_date} onChange={(e) => setExpForm((p) => ({ ...p, end_date: e.target.value }))} disabled={expForm.current} sx={{ ...(expDatesInvalid ? inputErrorSx : inputSx), opacity: expForm.current ? 0.4 : 1, cursor: expForm.current ? "not-allowed" : "auto" }} />
                  </Box>
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>{f("common.description")} *</Typography>
                  <Box component="textarea" value={expForm.description} onChange={(e) => setExpForm((p) => ({ ...p, description: e.target.value }))} onBlur={() => touchExpField("description")} rows={3} sx={{ ...(expFieldInvalid("description") ? inputErrorSx : inputSx), height: "auto", minHeight: "60px", py: "10px", resize: "none" }} />
                  {expFieldInvalid("description") && <FieldError message={f("required_field")} />}
                </Box>
                {expDatesInvalid && (
                  <Typography sx={{ fontSize: "12px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {f("validation_dates")}
                  </Typography>
                )}
                {expSubmitAttempted && expHasMissing && (
                  <Typography sx={{ fontSize: "12px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", display: "flex", alignItems: "center", gap: "6px" }}>
                    <AlertCircle size={12} /> {f("fix_highlighted_fields")}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box component="button" onClick={handleSaveExperience} disabled={savingExp} sx={{ ...btnPrimarySx, flex: 1, opacity: savingExp ? 0.5 : 1 }}>
                    {savingExp ? <CircularProgress size={12} sx={{ color: "hsl(0, 0%, 100%)" }} /> : (editingExpId ? <CheckCircle size={12} /> : <Plus size={12} />)} {f(editingExpId ? "common.save" : "common.add")}
                  </Box>
                  <Box component="button" onClick={resetExpForm} sx={btnSecondarySx}>{f("common.cancel")}</Box>
                </Box>
              </Box>
            ) : (
              <Box component="button" onClick={() => { setEditingExpId(null); setShowExpForm(true); }} sx={{ ...btnSecondarySx, width: "100%", mt: experiences.length ? "8px" : 0 }}>
                <Plus size={12} /> {f("add_experience")}
              </Box>
            )}
          </Section>

          {/* Education / Training */}
          <Section title={f("section_education")} id="education" icon={GraduationCap} expanded={expandedSections.has("education")} onToggle={toggleSection}>
            {trainings.map((t) => {
              if (showTrainingForm && editingTrainingId === t.id) return null;
              return (
                <Box key={t.id} sx={{ p: "12px", borderRadius: "12px", bgcolor: "hsla(160, 10%, 96%, 0.3)", mb: "8px", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px", color: "hsl(178, 93%, 10%)" }}>
                      {t.diploma}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                      {t.institution}{t.study_field ? ` · ${t.study_field}` : ""} · {formatDate(t.start_date)} — {t.end_date ? formatDate(t.end_date) : f("common.present")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                    <Box component="button" onClick={() => startEditTraining(t)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(178, 93%, 10%)", "&:hover": { color: "hsl(178, 60%, 30%)" } }}>
                      <Pencil size={12} />
                    </Box>
                    <Box component="button" onClick={() => requestDelete(() => handleDeleteTraining(t.id))} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(0, 65%, 51%)", "&:hover": { color: "hsl(0, 65%, 40%)" } }}>
                      <Trash2 size={12} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {showTrainingForm ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: "12px", borderRadius: "12px", border: "1px solid hsla(160, 10%, 90%, 0.6)" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "8px" }}>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("diploma")} *</Typography>
                    <TextField value={trainingForm.diploma || ""} onChange={(e) => setTrainingForm((p) => ({ ...p, diploma: e.target.value }))} onBlur={() => touchTrainingField("diploma")} fullWidth size="small" sx={trainingFieldInvalid("diploma") ? textFieldErrorSx : textFieldSx} />
                    {trainingFieldInvalid("diploma") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("institution")} *</Typography>
                    <TextField value={trainingForm.institution || ""} onChange={(e) => setTrainingForm((p) => ({ ...p, institution: e.target.value }))} onBlur={() => touchTrainingField("institution")} fullWidth size="small" sx={trainingFieldInvalid("institution") ? textFieldErrorSx : textFieldSx} />
                    {trainingFieldInvalid("institution") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("study_field")}</Typography>
                    <TextField value={trainingForm.study_field || ""} onChange={(e) => setTrainingForm((p) => ({ ...p, study_field: e.target.value }))} fullWidth size="small" sx={textFieldSx} />
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.location")} *</Typography>
                    <TextField value={trainingForm.location || ""} onChange={(e) => setTrainingForm((p) => ({ ...p, location: e.target.value }))} onBlur={() => touchTrainingField("location")} fullWidth size="small" sx={trainingFieldInvalid("location") ? textFieldErrorSx : textFieldSx} />
                    {trainingFieldInvalid("location") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.start_date")} *</Typography>
                    <Box component="input" type="date" value={trainingForm.start_date} onChange={(e) => setTrainingForm((p) => ({ ...p, start_date: e.target.value }))} onBlur={() => touchTrainingField("start_date")} sx={trainingFieldInvalid("start_date") ? inputErrorSx : inputSx} />
                    {trainingFieldInvalid("start_date") && <FieldError message={f("required_field")} />}
                  </Box>
                  <Box>
                    <Typography component="label" sx={labelSx}>{f("common.end_date")}</Typography>
                    <Box component="input" type="date" value={trainingForm.end_date} onChange={(e) => setTrainingForm((p) => ({ ...p, end_date: e.target.value }))} sx={trainingDatesInvalid ? inputErrorSx : inputSx} />
                  </Box>
                </Box>
                {trainingDatesInvalid && (
                  <Typography sx={{ fontSize: "12px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {f("validation_dates")}
                  </Typography>
                )}
                {trainingSubmitAttempted && trainingHasMissing && (
                  <Typography sx={{ fontSize: "12px", color: "hsl(0, 65%, 51%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", display: "flex", alignItems: "center", gap: "6px" }}>
                    <AlertCircle size={12} /> {f("fix_highlighted_fields")}
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box component="button" onClick={handleSaveTraining} disabled={savingTraining} sx={{ ...btnPrimarySx, flex: 1, opacity: savingTraining ? 0.5 : 1 }}>
                    {savingTraining ? <CircularProgress size={12} sx={{ color: "hsl(0, 0%, 100%)" }} /> : (editingTrainingId ? <CheckCircle size={12} /> : <Plus size={12} />)} {f(editingTrainingId ? "common.save" : "common.add")}
                  </Box>
                  <Box component="button" onClick={resetTrainingForm} sx={btnSecondarySx}>{f("common.cancel")}</Box>
                </Box>
              </Box>
            ) : (
              <Box component="button" onClick={() => { setEditingTrainingId(null); setShowTrainingForm(true); }} sx={{ ...btnSecondarySx, width: "100%", mt: trainings.length ? "8px" : 0 }}>
                <Plus size={12} /> {f("add_training")}
              </Box>
            )}
          </Section>

          {/* Hard Skills */}
          <Section title={f("section_hard_skills")} id="skills" icon={TrendingUp} expanded={expandedSections.has("skills")} onToggle={toggleSection}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: hardSkills.length ? "10px" : 0 }}>
              {hardSkills.map((s) => (
                <Box key={s} sx={skillChipSx("hard")}>
                  {getSkillLabel("hard", i18nLocale, s)}
                  <Box component="button" onClick={() => removeSkill(hardSkills, setHardSkills, s)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}>
                    <X size={10} />
                  </Box>
                </Box>
              ))}
            </Box>
            <SkillSelect candidateStyle selectedValues={hardSkills} category="hard" label={f("add_skill_placeholder")} onChange={handleSkillChange} />
          </Section>

          {/* Soft Skills */}
          <Section title={f("section_soft_skills")} id="softskills" icon={User} expanded={expandedSections.has("softskills")} onToggle={toggleSection}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: softSkills.length ? "10px" : 0 }}>
              {softSkills.map((s) => (
                <Box key={s} sx={skillChipSx("soft")}>
                  {getSkillLabel("soft", i18nLocale, s)}
                  <Box component="button" onClick={() => removeSkill(softSkills, setSoftSkills, s)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}>
                    <X size={10} />
                  </Box>
                </Box>
              ))}
            </Box>
            <SkillSelect candidateStyle selectedValues={softSkills} category="soft" label={f("add_soft_skill_placeholder")} onChange={handleSkillChange} />
          </Section>

          {/* Languages */}
          <Section title={f("section_languages")} id="languages" icon={Globe} expanded={expandedSections.has("languages")} onToggle={toggleSection}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: langSkills.length ? "10px" : 0 }}>
              {langSkills.map((s) => (
                <Box key={s} sx={skillChipSx("lang")}>
                  {getSkillLabel("lang", i18nLocale, s)}
                  <Box component="button" onClick={() => removeSkill(langSkills, setLangSkills, s)} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}>
                    <X size={10} />
                  </Box>
                </Box>
              ))}
            </Box>
            <SkillSelect candidateStyle selectedValues={langSkills} category="lang" label={f("add_language_placeholder")} onChange={handleSkillChange} />
          </Section>

          {/* Certifications */}
          <Section title={f("section_certifications")} id="certifications" icon={Award} expanded={expandedSections.has("certifications")} onToggle={toggleSection}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: certificates.length ? "10px" : 0 }}>
              {certificates.map((cert) => (
                <Box key={cert.id} sx={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  px: "12px", py: "6px", borderRadius: "9999px",
                  bgcolor: "hsla(40, 90%, 50%, 0.1)", color: "hsl(40, 90%, 35%)",
                  fontSize: "12px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                }}>
                  {getCertificateLabel(i18nLocale, cert.name)}
                  <Box component="button" onClick={() => requestDelete(() => handleDeleteCertificate(cert.id))} sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: 0, display: "flex", color: "inherit" }}>
                    <X size={10} />
                  </Box>
                </Box>
              ))}
            </Box>
            <CertificateSelect
              selectedIds={certificates.map((c) => c.name)}
              label={f("search_certification_placeholder")}
              disabled={savingCertificate}
              onChange={handleAddCertificate}
            />
          </Section>

          {/* Projects / Portfolio */}
          <Section title={f("section_projects")} id="projects" icon={FolderOpen} expanded={expandedSections.has("projects")} onToggle={toggleSection}>
            {projects.length === 0 && !showProjectForm && (
              <Box sx={{ textAlign: "center", py: "24px", color: "hsl(178, 10%, 55%)" }}>
                <FolderOpen size={32} style={{ opacity: 0.35, marginBottom: 8 }} />
                <Typography sx={{ fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>{f("no_projects")}</Typography>
              </Box>
            )}

            {projects.map((project) => (
              <Box key={project.id} sx={{ mb: "16px", p: "14px", borderRadius: "12px", border: "1px solid hsla(178, 93%, 10%, 0.1)", bgcolor: "hsla(178, 93%, 10%, 0.02)" }}>
                {editingProjectId === project.id ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Box>
                      <Typography component="label" sx={labelSx}>{f("project_title")}</Typography>
                      <TextField
                        value={projectForm.title || ""}
                        onChange={(e) => setProjectForm((f) => ({ ...f, title: e.target.value }))}
                        fullWidth size="small" sx={textFieldSx}
                      />
                    </Box>
                    <CustomSelect
                      label={f("project_type_label")}
                      options={projectTypeOptions}
                      value={projectForm.project_type}
                      onChange={(val) => setProjectForm((f) => ({ ...f, project_type: val }))}
                    />
                    <Box>
                      <Typography component="label" sx={labelSx}>{f("common.location")}</Typography>
                      <TextField
                        value={projectForm.location || ""}
                        onChange={(e) => setProjectForm((f) => ({ ...f, location: e.target.value }))}
                        fullWidth size="small" sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography component="label" sx={labelSx}>{f("common.end_date")}</Typography>
                      <TextField
                        type="date"
                        value={projectForm.end_date || ""}
                        onChange={(e) => setProjectForm((f) => ({ ...f, end_date: e.target.value }))}
                        fullWidth size="small" sx={textFieldSx}
                      />
                    </Box>
                    <Box>
                      <Typography component="label" sx={labelSx}>{f("common.description")}</Typography>
                      <TextField
                        multiline
                        rows={3}
                        value={projectForm.description || ""}
                        onChange={(e) => setProjectForm((f) => ({ ...f, description: e.target.value }))}
                        fullWidth size="small" sx={textAreaSx}
                      />
                    </Box>
                    {project.photo_urls?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "4px" }}>
                        {project.photo_urls.map((url) => {
                          const signedId = url.split("/").pop()?.split("?")[0];
                          return (
                            <Box key={url} sx={{ position: "relative" }}>
                              <Box component="img" src={url} alt="" sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: "8px", border: "1px solid hsla(178,93%,10%,0.12)" }} />
                              <Box
                                component="button"
                                onClick={() => requestDelete(() => handleDeleteProjectPhoto(project.id, signedId))}
                                sx={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", bgcolor: "hsl(0,65%,51%)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", p: 0, color: "#fff" }}
                              >
                                <X size={10} />
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    {renderPendingPhotoThumbs()}
                    <FileDropzone
                      label={f("project_photos_label")}
                      handleFileInput={(files) => handleUploadProjectPhoto(files)}
                      filesLimit={10}
                      accept={PROJECT_PHOTO_ACCEPT}
                      fileTypesLabel={f("project_photos_types")}
                      type="candidate"
                    />
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Box
                        component="button"
                        onClick={() => handleUpdateProject(project.id)}
                        disabled={savingProject || projectPhotosUploading || !projectForm.title.trim()}
                        sx={{ flex: 1, py: "8px", borderRadius: "8px", border: "none", cursor: "pointer", bgcolor: "hsl(153, 68%, 25%)", color: "#fff", fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", opacity: savingProject || !projectForm.title.trim() ? 0.5 : 1 }}
                      >
                        {savingProject ? f("common.saving") : f("common.save")}
                      </Box>
                      <Box
                        component="button"
                        onClick={() => { setEditingProjectId(null); setProjectForm({ title: "", project_type: "", location: "", end_date: "", description: "" }); clearPendingPhotos(); }}
                        sx={{ px: "14px", py: "8px", borderRadius: "8px", border: "1px solid hsla(178,93%,10%,0.2)", cursor: "pointer", bgcolor: "transparent", fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178,93%,10%)" }}
                      >{f("common.cancel")}</Box>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "6px" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "14px", color: "hsl(178, 93%, 10%)" }}>
                        {project.title}
                      </Typography>
                      <Box sx={{ display: "flex", gap: "6px" }}>
                        <Box
                          component="button"
                          onClick={() => { setEditingProjectId(project.id); clearPendingPhotos(); setProjectForm({ title: project.title || "", project_type: project.project_type || "", location: project.location || "", end_date: project.end_date || "", description: project.description || "" }); }}
                          sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(178, 93%, 10%)" }}
                        >
                          <Pencil size={14} />
                        </Box>
                        <Box
                          component="button"
                          onClick={() => requestDelete(() => handleDeleteProject(project.id))}
                          sx={{ border: "none", bgcolor: "transparent", cursor: "pointer", p: "4px", display: "flex", color: "hsl(0, 65%, 51%)" }}
                        >
                          <Trash2 size={14} />
                        </Box>
                      </Box>
                    </Box>
                    {project.project_type && (
                      <Typography sx={{ fontSize: "12px", color: "hsl(178, 60%, 30%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: "4px" }}>
                        {projectTypeOptions.find((o) => o.value === project.project_type)?.label || project.project_type}
                      </Typography>
                    )}
                    {project.location && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "4px" }}>
                        <MapPin size={11} color="hsl(178, 10%, 50%)" />
                        <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                          {project.location}
                        </Typography>
                      </Box>
                    )}
                    {project.end_date && (
                      <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 55%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mb: "4px" }}>
                        {formatDate(project.end_date)}
                      </Typography>
                    )}
                    {project.description && (
                      <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 40%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "6px" }}>
                        {project.description}
                      </Typography>
                    )}
                    {project.photo_urls?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mt: "10px" }}>
                        {project.photo_urls.map((url) => (
                          <Box key={url} component="img" src={url} alt="" sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: "8px", border: "1px solid hsla(178,93%,10%,0.12)" }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            ))}

            {showProjectForm && editingProjectId === null && (
              <Box sx={{ p: "14px", borderRadius: "12px", border: "1px dashed hsla(178,93%,10%,0.2)", bgcolor: "hsla(178,93%,10%,0.015)", display: "flex", flexDirection: "column", gap: "10px", mb: "12px" }}>
                <Box>
                  <Typography component="label" sx={labelSx}>{f("project_title")}</Typography>
                  <TextField
                    value={projectForm.title || ""}
                    onChange={(e) => setProjectForm((f) => ({ ...f, title: e.target.value }))}
                    fullWidth size="small" sx={textFieldSx}
                  />
                </Box>
                <CustomSelect
                  label={f("project_type_label")}
                  options={projectTypeOptions}
                  value={projectForm.project_type}
                  onChange={(val) => setProjectForm((f) => ({ ...f, project_type: val }))}
                />
                <Box>
                  <Typography component="label" sx={labelSx}>{f("common.location")}</Typography>
                  <TextField
                    value={projectForm.location || ""}
                    onChange={(e) => setProjectForm((f) => ({ ...f, location: e.target.value }))}
                    fullWidth size="small" sx={textFieldSx}
                  />
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>{f("common.end_date")}</Typography>
                  <TextField
                    type="date"
                    value={projectForm.end_date || ""}
                    onChange={(e) => setProjectForm((f) => ({ ...f, end_date: e.target.value }))}
                    fullWidth size="small" sx={textFieldSx}
                  />
                </Box>
                <Box>
                  <Typography component="label" sx={labelSx}>{f("common.description")}</Typography>
                  <TextField
                    multiline
                    rows={3}
                    value={projectForm.description || ""}
                    onChange={(e) => setProjectForm((f) => ({ ...f, description: e.target.value }))}
                    fullWidth size="small" sx={textAreaSx}
                  />
                </Box>
                {renderPendingPhotoThumbs()}
                <FileDropzone
                  label={f("project_photos_label")}
                  handleFileInput={(files) => handleUploadProjectPhoto(files)}
                  filesLimit={10}
                  accept={PROJECT_PHOTO_ACCEPT}
                  fileTypesLabel={f("project_photos_types")}
                  type="candidate"
                />
                <Box sx={{ display: "flex", gap: "8px" }}>
                  <Box
                    component="button"
                    onClick={handleAddProject}
                    disabled={savingProject || !projectForm.title.trim()}
                    sx={{ flex: 1, py: "8px", borderRadius: "8px", border: "none", cursor: "pointer", bgcolor: "hsl(153, 68%, 25%)", color: "#fff", fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", opacity: savingProject || !projectForm.title.trim() ? 0.5 : 1 }}
                  >
                    {savingProject ? f("common.saving") : f("add_project_btn")}
                  </Box>
                  <Box
                    component="button"
                    onClick={() => { setShowProjectForm(false); setProjectForm({ title: "", project_type: "", location: "", end_date: "", description: "" }); clearPendingPhotos(); }}
                    sx={{ px: "14px", py: "8px", borderRadius: "8px", border: "1px solid hsla(178,93%,10%,0.2)", cursor: "pointer", bgcolor: "transparent", fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178,93%,10%)" }}
                  >{f("common.cancel")}</Box>
                </Box>
              </Box>
            )}

            {!showProjectForm && editingProjectId === null && (
              <Box
                component="button"
                onClick={() => { setShowProjectForm(true); setProjectForm({ title: "", project_type: "", location: "", end_date: "", description: "" }); clearPendingPhotos(); }}
                sx={{ display: "flex", alignItems: "center", gap: "6px", border: "1px dashed hsla(178,93%,10%,0.25)", borderRadius: "8px", bgcolor: "transparent", cursor: "pointer", px: "14px", py: "8px", color: "hsl(178, 60%, 30%)", fontSize: "13px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", width: "100%" }}
              >
                <Plus size={14} />
                {f("add_project")}
              </Box>
            )}
          </Section>

          {/* Share projects */}
          <Section title={f("section_share_projects")} id="projects_share" icon={Share2} expanded={expandedSections.has("projects_share")} onToggle={toggleSection}>
            <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", mb: "12px" }}>
              {f("share_projects_description")}
            </Typography>
            <Box
              component="button"
              type="button"
              disabled={togglingShare}
              onClick={() => handleToggleProjectsSharing(!currentCandidate?.projects_public)}
              sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", border: "none", bgcolor: "transparent", cursor: togglingShare ? "default" : "pointer", p: 0, textAlign: "left", opacity: togglingShare ? 0.6 : 1 }}
            >
              <Box sx={{ position: "relative", width: "36px", height: "20px", borderRadius: "9999px", flexShrink: 0, bgcolor: currentCandidate?.projects_public ? "hsl(153, 68%, 25%)" : "hsla(160, 10%, 90%, 1)", transition: "background-color 0.2s" }}>
                <Box sx={{ position: "absolute", top: "2px", left: currentCandidate?.projects_public ? "18px" : "2px", width: "16px", height: "16px", borderRadius: "9999px", bgcolor: "hsl(0, 0%, 100%)", boxShadow: "0 1px 3px hsla(0,0%,0%,0.2)", transition: "left 0.2s" }} />
              </Box>
              <Typography sx={{ fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                {f("share_projects_toggle")}
              </Typography>
            </Box>
            {currentCandidate?.projects_public && publicProjectsUrl && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "12px", p: "8px 12px", borderRadius: "8px", border: "1px solid hsla(160, 10%, 90%, 0.8)", bgcolor: "hsla(160, 10%, 96%, 0.4)" }}>
                <LinkIcon size={14} color="hsl(178, 60%, 30%)" />
                <Typography sx={{ flex: 1, fontSize: "12px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(178, 60%, 30%)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {publicProjectsUrl}
                </Typography>
                <Box
                  component="button"
                  onClick={handleCopyProjectsLink}
                  sx={{ display: "flex", alignItems: "center", gap: "4px", border: "none", bgcolor: "transparent", cursor: "pointer", color: "hsl(153, 68%, 25%)", fontSize: "12px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", flexShrink: 0 }}
                >
                  {copiedLink ? <CheckCircle size={14} /> : <Copy size={14} />}
                  {f(copiedLink ? "share_projects_copied" : "share_projects_copy")}
                </Box>
              </Box>
            )}
          </Section>
        </Box>


        {/* Recruiter preview */}
        <Box sx={{ display: mobileView === "edit" ? { xs: "none", lg: "block" } : "block" }}>
          <Box sx={{ position: "sticky", top: "90px", display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* Preview label */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Eye size={12} color="hsl(178, 10%, 45%)" />
                <Typography sx={{ fontSize: "10px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(178, 10%, 45%)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{f("recruiter_preview")}</Typography>
              </Box>
              <Typography sx={{ fontSize: "10px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "4px" }}>{f("recruiter_preview_subtitle")}</Typography>
            </Box>

            {/* Profile header */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => photoInputRef.current?.click()}
                sx={{
                  width: "64px", height: "64px", borderRadius: "9999px", overflow: "hidden", flexShrink: 0,
                  bgcolor: "hsla(153, 68%, 25%, 0.1)",
                  border: "2px solid hsla(160, 10%, 90%, 0.8)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", p: 0, position: "relative",
                  "&:hover .photo-overlay-preview": { opacity: 1 },
                  "&:hover": { borderColor: "hsla(153, 68%, 25%, 0.5)" },
                  transition: "border-color 0.15s",
                }}
              >
                {currentCandidate.profile_picture_url ? (
                  <Box component="img" src={currentCandidate.profile_picture_url} alt="Photo de profil" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Typography sx={{ fontSize: "20px", fontFamily: "Segoe UI Bold, sans-serif, system-ui", color: "hsl(153, 68%, 25%)" }}>
                    {(form.first_name || "P")[0]}{(form.last_name || "N")[0]}
                  </Typography>
                )}
                <Box className="photo-overlay-preview" sx={{
                  position: "absolute", inset: 0, borderRadius: "9999px",
                  bgcolor: "hsla(0, 0%, 0%, 0.45)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  opacity: 0, transition: "opacity 0.15s",
                }}>
                  <Camera size={16} color="hsl(0, 0%, 100%)" />
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "20px", color: "hsl(178, 93%, 10%)", lineHeight: 1.2 }}>
                  {form.first_name || f("first_name_placeholder")} {form.last_name || f("last_name_placeholder")}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mt: "2px" }}>
                  {form.job_title || f("title_not_provided")}
                </Typography>
                {form.city && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "10px", py: "4px", borderRadius: "9999px", border: "1px solid hsla(160, 10%, 90%, 0.6)", fontSize: "11px", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(178, 93%, 10%)" }}>
                      {form.city}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Contact card */}
            <Box sx={{ borderRadius: "12px", border: "1px solid hsla(160, 10%, 90%, 0.6)", p: "20px" }}>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "15px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("contact")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  { icon: Mail, text: currentCandidate.email || f("common.not_provided") },
                  { icon: Phone, text: form.phone || currentCandidate.phone || f("common.not_provided") },
                  { icon: MapPin, text: f("region_prefix", { value: form.region || form.city || f("common.not_provided") }) },
                  { icon: Briefcase, text: form.experience_year ? f("years_experience_value", { count: form.experience_year }) : f("experience_not_provided") },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <item.icon size={15} color="hsl(178, 93%, 10%)" />
                    <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Preferences card */}
            <Box sx={{ borderRadius: "12px", border: "1px solid hsla(160, 10%, 90%, 0.6)", p: "20px" }}>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "15px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_preferences")}</Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {f("position_prefix", { value: form.desired_job_title || f("common.not_provided") })}
                </Typography>
                <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {f("location_prefix", { value: desiredLocations.length ? desiredLocations.map((l) => l.label).join(", ") : f("common.not_provided") })}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "8px", mt: "16px" }}>
                <Box
                  component="button"
                  onClick={() => {
                    if (currentCandidate.resume_url) window.open(currentCandidate.resume_url, "_blank", "noopener");
                    else openSection("documents");
                  }}
                  sx={{
                    display: "flex", alignItems: "center", gap: "8px",
                    px: "16px", py: "10px", borderRadius: "12px",
                    border: currentCandidate.resume_url ? "1px solid hsla(153, 68%, 25%, 0.3)" : "1px solid hsla(160, 10%, 90%, 0.6)",
                    bgcolor: currentCandidate.resume_url ? "hsla(153, 68%, 25%, 0.05)" : "transparent",
                    color: currentCandidate.resume_url ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)",
                    fontSize: "13px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.5)" },
                    transition: "all 0.15s",
                  }}
                >
                  <Download size={14} /> {f("download_cv")}
                </Box>
              </Box>
            </Box>

            {/* Présentation */}
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("presentation")}</Typography>
              {form.presentation ? (
                <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", lineHeight: 1.6 }}>
                  {form.presentation}
                </Typography>
              ) : (
                <Box sx={{ p: "20px", borderRadius: "12px", border: "1px dashed hsla(160, 10%, 90%, 0.6)", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontStyle: "italic" }}>{f("no_presentation")}</Typography>
                </Box>
              )}
            </Box>

            {/* Hard Skills preview */}
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_hard_skills")}</Typography>
              {hardSkills.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {hardSkills.map((s) => <Box key={s} sx={previewSkillSx("hard")}>{getSkillLabel("hard", i18nLocale, s)}</Box>)}
                </Box>
              ) : <EmptyPreview text={f("no_hard_skills")} />}
            </Box>

            {/* Soft Skills preview */}
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_soft_skills")}</Typography>
              {softSkills.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {softSkills.map((s) => <Box key={s} sx={previewSkillSx("soft")}>{getSkillLabel("soft", i18nLocale, s)}</Box>)}
                </Box>
              ) : <EmptyPreview text={f("no_soft_skills")} />}
            </Box>

            {/* Languages preview */}
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_languages")}</Typography>
              {langSkills.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {langSkills.map((s) => <Box key={s} sx={previewSkillSx("lang")}>{getSkillLabel("lang", i18nLocale, s)}</Box>)}
                </Box>
              ) : <EmptyPreview text={f("no_languages")} />}
            </Box>

            {/* Certifications preview */}
            <Box>
              <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_certifications")}</Typography>
              {certificates.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {certificates.map((cert) => (
                    <Box key={cert.id} sx={{
                      px: "14px", py: "8px", borderRadius: "9999px",
                      bgcolor: "hsla(40, 90%, 50%, 0.12)", color: "hsl(40, 90%, 30%)",
                      fontSize: "13px", fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    }}>
                      {getCertificateLabel(i18nLocale, cert.name)}
                    </Box>
                  ))}
                </Box>
              ) : <EmptyPreview text={f("no_certifications")} />}
            </Box>

            {/* Projects preview */}
            {projects.length > 0 && (
              <Box>
                <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_projects")}</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  {projects.map((project) => (
                    <Box key={project.id} sx={{ pl: "16px", borderLeft: "2px solid hsla(178, 93%, 10%, 0.3)" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px", color: "hsl(178, 93%, 10%)" }}>
                        {project.title}
                      </Typography>
                      {project.project_type && (
                        <Typography sx={{ fontSize: "12px", color: "hsl(178, 60%, 30%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                          {projectTypeOptions.find((o) => o.value === project.project_type)?.label || project.project_type}
                        </Typography>
                      )}
                      {(project.location || project.end_date) && (
                        <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                          {[project.location, project.end_date ? formatDate(project.end_date) : null].filter(Boolean).join(" · ")}
                        </Typography>
                      )}
                      {project.description && (
                        <Typography sx={{ fontSize: "12px", color: "hsl(178, 10%, 40%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", mt: "4px" }}>
                          {project.description}
                        </Typography>
                      )}
                      {project.photo_urls?.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
                          {project.photo_urls.map((url) => (
                            <Box key={url} component="img" src={url} alt="" sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: "6px", border: "1px solid hsla(178,93%,10%,0.12)" }} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Education preview */}
            {trainings.length > 0 && (
              <Box>
                <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_education")}</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {trainings.map((t) => (
                    <Box key={t.id} sx={{ pl: "16px", borderLeft: "2px solid hsla(178, 93%, 10%, 0.3)" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px", color: "hsl(178, 93%, 10%)" }}>
                        {t.diploma}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "hsl(178, 93%, 10%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                        {t.institution}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {formatDate(t.start_date)} — {t.end_date ? formatDate(t.end_date) : f("common.present")}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Experience preview */}
            {experiences.length > 0 && (
              <Box>
                <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "17px", color: "hsl(178, 93%, 10%)", mb: "12px" }}>{f("section_experience")}</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {experiences.map((exp) => (
                    <Box key={exp.id} sx={{ pl: "16px", borderLeft: "2px solid hsla(153, 68%, 25%, 0.3)" }}>
                      <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px", color: "hsl(178, 93%, 10%)" }}>
                        {exp.title}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "hsl(153, 68%, 25%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
                        {exp.company}
                      </Typography>
                      <Typography sx={{ fontSize: "11px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {formatDate(exp.start_date)} — {exp.end_date ? formatDate(exp.end_date) : f("common.present")}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

          </Box>
        </Box>
      </Box>

      {/* Confirm destructive actions */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <Box sx={{ p: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "16px", color: "hsl(178, 93%, 10%)" }}>
            {f("confirm_delete_title")}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", lineHeight: 1.6 }}>
            {f("confirm_delete_message")}
          </Typography>
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box
              component="button"
              onClick={() => setConfirmDelete(null)}
              sx={{ ...btnSecondarySx, flex: 1, py: "12px" }}
            >
              {f("common.cancel")}
            </Box>
            <Box
              component="button"
              onClick={() => { const action = confirmDelete?.run; setConfirmDelete(null); if (action) action(); }}
              sx={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                py: "12px", borderRadius: "12px", border: "none", cursor: "pointer",
                bgcolor: "hsl(0, 65%, 51%)", color: "hsl(0, 0%, 100%)",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "12px",
                "&:hover": { bgcolor: "hsl(0, 65%, 45%)" },
                transition: "all 0.15s",
              }}
            >
              <Trash2 size={12} />
              {f("confirm_delete_confirm")}
            </Box>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

const Section = ({ title, id, icon: Icon, expanded, onToggle, children }) => (
  <Box id={`section-${id}`} sx={{ bgcolor: "hsl(0, 0%, 100%)", borderRadius: "16px", border: "1px solid hsla(160, 10%, 90%, 0.6)", overflow: "hidden" }}>
    <Box component="button" onClick={() => onToggle(id)} sx={{ ...{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", p: "16px", cursor: "pointer", border: "none", bgcolor: "transparent", "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.3)" }, transition: "background 0.15s" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "hsla(160, 10%, 96%, 0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={14} color="hsl(178, 10%, 45%)" />
        </Box>
        <Typography sx={{ fontFamily: "Segoe UI Bold, sans-serif, system-ui", fontSize: "13px", color: "hsl(178, 93%, 10%)" }}>
          {title}
        </Typography>
      </Box>
      {expanded ? <ChevronUp size={14} color="hsl(178, 10%, 45%)" /> : <ChevronDown size={14} color="hsl(178, 10%, 45%)" />}
    </Box>
    <AnimatePresence>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Box sx={{ px: "16px", pb: "16px", pt: "16px", borderTop: "1px solid hsla(160, 10%, 90%, 0.6)" }}>
            {children}
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  </Box>
);

const CustomSelect = ({ value, onChange, options }) => {
  const isTouch = useMediaQuery("(max-width:599px)");
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleDown = (e) => {
      if (!triggerRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const handleScroll = () => setOpen(false);
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [open]);

  const handleTrigger = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen((v) => !v);
  };

  const selected = options.find((o) => o.value === value);

  if (isTouch) {
    return (
      <Box
        component="select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        sx={{ ...inputSx, appearance: "auto", cursor: "pointer", pr: "10px" }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Box ref={triggerRef}>
        <Box
          component="button"
          type="button"
          onClick={handleTrigger}
          sx={{
            ...inputSx,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            cursor: "pointer", textAlign: "left",
            border: open ? "1px solid hsla(153, 68%, 25%, 0.4)" : "1px solid hsla(160, 10%, 90%, 0.6)",
            pr: "10px",
          }}
        >
          <Typography component="span" sx={{ fontSize: "13px", fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: selected?.value ? "hsl(178, 93%, 10%)" : "hsl(178, 10%, 55%)" }}>
            {selected?.label ?? "—"}
          </Typography>
          <Box sx={{ flexShrink: 0, display: "flex", transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
            <ChevronDown size={13} color="hsl(178, 10%, 45%)" />
          </Box>
        </Box>
      </Box>
      {open && createPortal(
        <Box
          ref={dropdownRef}
          sx={{
            position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999,
            bgcolor: "hsl(0, 0%, 100%)", borderRadius: "12px",
            border: "1px solid hsla(160, 10%, 90%, 0.8)",
            boxShadow: "0 8px 24px hsla(0,0%,0%,0.1)",
            overflow: "hidden", maxHeight: "240px", overflowY: "auto",
            py: "4px",
          }}
        >
          {options.map((opt) => (
            <Box
              key={opt.value}
              component="button"
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", px: "14px", py: "9px",
                border: "none", cursor: "pointer", textAlign: "left",
                bgcolor: opt.value === value ? "hsla(153, 68%, 25%, 0.08)" : "transparent",
                color: opt.value === value ? "hsl(153, 68%, 25%)" : opt.value === "" ? "hsl(178, 10%, 55%)" : "hsl(178, 93%, 10%)",
                fontFamily: opt.value === value ? "Segoe UI Bold, sans-serif, system-ui" : "Segoe UI Regular, sans-serif, system-ui",
                fontSize: "13px",
                "&:hover": { bgcolor: opt.value === value ? "hsla(153, 68%, 25%, 0.12)" : "hsla(160, 10%, 96%, 0.6)" },
                transition: "background 0.1s",
                boxSizing: "border-box",
              }}
            >
              <Typography component="span" sx={{ fontSize: "inherit", fontFamily: "inherit", color: "inherit" }}>
                {opt.label}
              </Typography>
              {opt.value !== "" && opt.value === value && (
                <CheckCircle size={12} style={{ flexShrink: 0 }} />
              )}
            </Box>
          ))}
        </Box>,
        document.body
      )}
    </>
  );
};

const EmptyPreview = ({ text }) => (
  <Typography sx={{ fontSize: "13px", color: "hsl(178, 10%, 45%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontStyle: "italic" }}>
    {text}
  </Typography>
);

export default ProfileTab;
